// Kael 스킬 타임라인(D1c, COMBAT-FEEL 2.10·2.11, PRD 3A.3). 스킬 수치·판정·연출 큐의 정본이다.
// config.ts의 SKILLS·LINK.cancelAt·SKILL_TUNING·SKILL_PULL은 이 데이터에서 만든다. 게임 로직(hero.ts)이 이 데이터를 읽어 실행한다.
// 기존 네 스킬(피의 돌진·칼날 폭풍·붉은 낙화·진홍 초승달)의 동작·수치는 이전 config 값 그대로다(테스트 skill-timeline.test.ts가 고정).
// 시간 값은 틱(ms()로 적은 값은 밀리초 → 틱). 이 파일은 config.ts를 import하지 않는다(config가 이 파일을 import하므로 순환 방지).
import { CRESCENT } from '../growth';
import { ms } from '../ticks';
import { SKILL_UNLOCK_LEVEL, type SkillTimeline } from './types';

/** 스킬 세부 동작(COMBAT-FEEL 1.1). config.SKILL_TUNING의 정본 */
export const KAEL_TUNING = {
  /** 칼날 폭풍 다단 히트 간격 */
  stormHitInterval: ms(150),
  /** 피의 돌진 이동 거리(m) */
  dashDistance: 7,
  /** 붉은 낙화 도약 높이(m) */
  fallHeight: 2.5,
  /** 진홍 초승달 선딜(검기가 나가는 틱) */
  crescentWindup: ms(150),
} as const;

/** 스킬 흡입(몹몰이, LIVE-FUN-PLAN 3.0). config.SKILL_PULL의 정본. speed는 m/s */
export const KAEL_PULL = {
  // 2026-10-20 실입력 비교: 반경 7m는 9~12m에서 몰려오는 무리에 닿지 않아 차이가 작았다(첫 접촉 시전 2마리 대 0마리) → 9m·12m/s
  storm: { radius: 9, speed: 12 },
  leap: { radius: 8, speed: 18, leadTicks: ms(250) },
} as const;

const DASH_DURATION = ms(420);
const STORM_DURATION = ms(900);
const FALL_DURATION = ms(650);
const CRESCENT_DURATION = ms(550);
const LAUNCHER_DURATION = ms(500);
/** 런처 판정 틱: 선딜 150ms */
const LAUNCHER_HIT_TICK = ms(150);

// C9 첫날 새 스킬(Lv15·20·25, content/heroes.ts 카엘 행 = PRD 3A.1). 셋 다 기존 다섯 스킬과 역할이 겹치지 않게 잡았다:
// 6 공중 난무 = 런처 뒤 공중 콤보(띄운 몹을 공중에 붙잡아 두는 다단 + 착지 마무리), 7 대지 가르기 = 전방 10m 균열(일직선 무리 처리·다운 추격),
// 8 혈검 회전베기 = 앞으로 밀고 나가는 회전 다단(슈퍼아머 — 보스 강타에도 안 끊긴다). 수치 근거는 docs/SKILL-TREE.md 12절(C9).
/** 공중 난무: 0.8초, 높이 1.8m. 8~32틱 6틱마다 전방 220° 5타(공중 유지 부여) → 마지막 틱 착지 원형 마무리 */
const AIR_DURATION = ms(800);
const AIR_FIRST = ms(130);
const AIR_EVERY = ms(100);
const AIR_LAST = AIR_FIRST + AIR_EVERY * 4;
/** 대지 가르기: 선딜 300ms에 내려찍고 균열이 70ms마다 2.5m씩 앞으로 번진다(0~10m) */
const SPLIT_DURATION = ms(750);
const SPLIT_IMPACT = ms(300);
const SPLIT_STEP = ms(70);
const SPLIT_SEG = 2.5;
/** 혈검 회전베기: 1.1초 동안 4.5m 전진하며 120ms마다 원형 9타, 반경 7m 몹을 끌어온다 */
const SPIN_DURATION = ms(1100);
const SPIN_EVERY = ms(120);

export const KAEL_SKILLS: readonly SkillTimeline[] = [
  {
    id: 1, key: 'U', name: '피의 돌진', clip: 'dash',
    cooldown: ms(5000), mpCost: 35, duration: DASH_DURATION, cancelAt: 0.55,
    unlock: { kind: 'free' },
    motion: { kind: 'dash', distance: KAEL_TUNING.dashDistance },
    // 지나간 경로(선분) 판정. 폭 3.6m = 반폭 1.8m. 참격 표시는 돌진 시작 틱에만
    hitboxes: [{ startTick: 0, endTick: DASH_DURATION - 1, shape: { kind: 'path', width: 3.6 }, mult: 2.4, slash: { range: 1.8, arcDeg: 90, finisher: false, firstOnly: true }, burst: 'dashWake' }],
    // vfx(c012): 출발 발차기 고리 + 몸 뒤로 뻗는 속도선(6틱마다). 속도선은 그 틱의 실제 영웅 위치에서 뒤로 그려 벽 너머로 나가지 않는다
    cues: [
      { tick: 0, channel: 'vfx', name: 'dash_kick' },
      { tick: 0, channel: 'sfx', name: 'skill_swing' },
      { tick: 6, channel: 'vfx', name: 'dash_streak' },
      { tick: 12, channel: 'vfx', name: 'dash_streak' },
      { tick: 18, channel: 'vfx', name: 'dash_streak' },
    ],
    props: { superArmor: false },
  },
  {
    id: 2, key: 'I', name: '칼날 폭풍', clip: 'atk4',
    cooldown: ms(8000), mpCost: 50, duration: STORM_DURATION, cancelAt: 0.45,
    unlock: { kind: 'free' },
    // 도는 동안 반경 안 몹을 계속 당긴다
    pull: { radius: KAEL_PULL.storm.radius, speed: KAEL_PULL.storm.speed, fromTick: 0, toTick: STORM_DURATION },
    // 150ms마다 원형 판정 6회(마지막 타격만 다단 중간타가 아니다)
    hitboxes: [{ startTick: 0, endTick: STORM_DURATION - 1, everyTicks: KAEL_TUNING.stormHitInterval, shape: { kind: 'circle', radius: 5 }, mult: 1.1, slash: { range: 5, arcDeg: 360, finisher: false }, burst: 'stormEye' }],
    // vfx(c012): 시전 순간 흡입 소용돌이 + 얇은 파동, 마지막 판정 틱(45) 마무리 고리
    cues: [
      { tick: 0, channel: 'vfx', name: 'storm_range' },
      { tick: 0, channel: 'sfx', name: 'skill_swing' },
      { tick: 45, channel: 'vfx', name: 'storm_finish' },
    ],
    props: { superArmor: false },
  },
  {
    id: 3, key: 'O', name: '붉은 낙화', clip: 'leap',
    cooldown: ms(11000), mpCost: 65, duration: FALL_DURATION, cancelAt: 0.6,
    unlock: { kind: 'free' },
    motion: { kind: 'jump', height: KAEL_TUNING.fallHeight },
    // 착지 직전(leadTicks) 반경 안 몹을 착지점으로 모은다
    pull: { radius: KAEL_PULL.leap.radius, speed: KAEL_PULL.leap.speed, fromTick: FALL_DURATION - 1 - KAEL_PULL.leap.leadTicks, toTick: FALL_DURATION - 1 },
    // 마지막 틱(착지)에 원형 판정 + 띄우기(마무리 계열)
    hitboxes: [{ startTick: FALL_DURATION - 1, endTick: FALL_DURATION - 1, shape: { kind: 'circle', radius: 4.5 }, mult: 3.2, grants: { finisher: true }, slash: { range: 4.5, arcDeg: 360, finisher: true }, burst: 'aftershock' }],
    cues: [
      { tick: 0, channel: 'vfx', name: 'fall_leap' },
      { tick: 0, channel: 'sfx', name: 'skill_swing' },
      { tick: FALL_DURATION - 1, channel: 'vfx', name: 'fall_shockwave' },
      { tick: FALL_DURATION - 1, channel: 'sfx', name: 'skill3_land' },
      { tick: FALL_DURATION - 1, channel: 'camera', name: 'punch_heavy' },
    ],
    props: { superArmor: false },
  },
  {
    id: 4, key: 'L', name: '진홍 초승달', clip: 'atk2',
    cooldown: ms(7000), mpCost: 55, duration: CRESCENT_DURATION, cancelAt: 0.5,
    // 2026-10-22 c008: 진홍 초승달은 영웅 Lv10에 자동 습득한다(트리 노드는 강화, docs/SKILL-TREE.md 11.1)
    unlock: { kind: 'level', level: SKILL_UNLOCK_LEVEL.crescent },
    // 선딜 뒤 발사 순간의 길로 검기(세계 투사체)가 나간다. 투사체 한 발은 영웅 동작과 무관하게 끝까지 날아간다
    hitboxes: [{ startTick: KAEL_TUNING.crescentWindup, endTick: KAEL_TUNING.crescentWindup, shape: { kind: 'wave', length: CRESCENT.length, width: CRESCENT.width, travelTicks: CRESCENT.travelTicks }, mult: 2.8, burst: 'twinMoon' }],
    cues: [
      { tick: 0, channel: 'vfx', name: 'crescent_charge' },
      { tick: KAEL_TUNING.crescentWindup, channel: 'vfx', name: 'crescent_wave' },
      { tick: KAEL_TUNING.crescentWindup, channel: 'sfx', name: 'skill4_wave' },
    ],
    props: { superArmor: false },
  },
  {
    // 천공 올려베기: Lv5 런처(COMBAT-FEEL 2.11). 맞은 일반 몹을 띄워 공중 추가타 → 착지 다운 → 다운 추격으로 잇는다
    id: 5, key: 'P', name: '천공 올려베기', clip: 'atk2',
    cooldown: ms(6000), mpCost: 40, duration: LAUNCHER_DURATION, cancelAt: 0.5,
    unlock: { kind: 'level', level: SKILL_UNLOCK_LEVEL.launcher },
    hitboxes: [{
      startTick: LAUNCHER_HIT_TICK, endTick: LAUNCHER_HIT_TICK,
      shape: { kind: 'cone', range: 3.0, arcDeg: 140 }, mult: 1.8,
      grants: { launch: { lift: 3.0, airTicks: ms(800), pull: 0.6 } },
      slash: { range: 3.0, arcDeg: 140, finisher: false },
    }],
    cues: [
      { tick: 0, channel: 'vfx', name: 'launcher_charge' },
      { tick: 0, channel: 'sfx', name: 'launcher_windup' },
      { tick: LAUNCHER_HIT_TICK, channel: 'vfx', name: 'launcher_arc_up' },
      { tick: LAUNCHER_HIT_TICK, channel: 'sfx', name: 'launcher_hit' },
      { tick: LAUNCHER_HIT_TICK, channel: 'camera', name: 'punch_mid' },
    ],
    props: { launcher: true, downChase: true },
  },
  {
    // 공중 난무(Lv15, 'air'): 천공 올려베기(5)로 띄운 몹을 따라 뛰어올라 공중에서 다섯 번 벤다. 맞은 일반 몹은 다시 짧게 띄워 공중에 붙잡아 둔다.
    // 착지 틱은 새 판정 묶음으로 원형 마무리(띄우기·무거운 처치). 연계: 5 → 6 → 3(낙화)·7(가르기)
    id: 6, key: '', name: '공중 난무', clip: 'leap',
    cooldown: ms(9000), mpCost: 55, duration: AIR_DURATION, cancelAt: 0.55,
    unlock: { kind: 'level', level: SKILL_UNLOCK_LEVEL.airDance },
    motion: { kind: 'jump', height: 1.8 },
    // 뛰어오르는 첫 0.3초 동안 가까운 몹을 발밑으로 모은다(공중 다단이 비지 않게)
    pull: { radius: 5.5, speed: 7, fromTick: 0, toTick: ms(300) },
    hitboxes: [
      {
        startTick: AIR_FIRST, endTick: AIR_LAST, everyTicks: AIR_EVERY,
        shape: { kind: 'cone', range: 3.4, arcDeg: 220 }, mult: 0.85,
        grants: { launch: { lift: 2.2, airTicks: ms(450), pull: 0.3 } },
        slash: { range: 3.4, arcDeg: 220, finisher: false },
      },
      {
        startTick: AIR_DURATION - 1, endTick: AIR_DURATION - 1, freshPulse: true,
        shape: { kind: 'circle', radius: 3.8 }, mult: 1.9, grants: { finisher: true },
        slash: { range: 3.8, arcDeg: 360, finisher: true },
      },
    ],
    cues: [
      { tick: 0, channel: 'vfx', name: 'air_rise' },
      { tick: 0, channel: 'sfx', name: 'skill_swing' },
      { tick: AIR_FIRST, channel: 'vfx', name: 'air_slash' },
      { tick: AIR_FIRST + AIR_EVERY * 2, channel: 'vfx', name: 'air_slash' },
      { tick: AIR_LAST, channel: 'vfx', name: 'air_slash' },
      { tick: AIR_FIRST, channel: 'sfx', name: 'air_slash' },
      { tick: AIR_LAST, channel: 'sfx', name: 'air_slash' },
      { tick: AIR_DURATION - 1, channel: 'vfx', name: 'air_land' },
      { tick: AIR_DURATION - 1, channel: 'sfx', name: 'skill3_land' },
      { tick: AIR_DURATION - 1, channel: 'camera', name: 'punch_mid' },
    ],
    props: { superArmor: false },
  },
  {
    // 대지 가르기(Lv20, 'downHit'): 대검을 내리꽂아 전방 10m 균열을 낸다. 발밑 내려찍기(마무리 계열 = 쓰러진 몹도 다시 띄운다) 뒤
    // 균열이 4구간으로 번진다(같은 몹은 한 번만). 일직선으로 몰려오는 무리·도망가는 원거리 몹 처리용
    id: 7, key: '', name: '대지 가르기', clip: 'atk3',
    cooldown: ms(10000), mpCost: 60, duration: SPLIT_DURATION, cancelAt: 0.5,
    unlock: { kind: 'level', level: SKILL_UNLOCK_LEVEL.groundSplit },
    hitboxes: [
      { startTick: SPLIT_IMPACT, endTick: SPLIT_IMPACT, shape: { kind: 'circle', radius: 2.6 }, mult: 1.4, grants: { finisher: true }, slash: { range: 2.6, arcDeg: 360, finisher: true } },
      ...[0, 1, 2, 3].map((i) => ({
        startTick: SPLIT_IMPACT + SPLIT_STEP * i, endTick: SPLIT_IMPACT + SPLIT_STEP * i,
        shape: { kind: 'line' as const, from: SPLIT_SEG * i, to: SPLIT_SEG * (i + 1), width: 3.0 }, mult: 3.0, grants: { finisher: true },
      })),
    ],
    cues: [
      { tick: 0, channel: 'vfx', name: 'split_charge' },
      { tick: 0, channel: 'sfx', name: 'skill_swing' },
      { tick: SPLIT_IMPACT, channel: 'vfx', name: 'split_impact' },
      { tick: SPLIT_IMPACT, channel: 'sfx', name: 'split_impact' },
      { tick: SPLIT_IMPACT, channel: 'camera', name: 'punch_heavy' },
      { tick: SPLIT_IMPACT + SPLIT_STEP, channel: 'vfx', name: 'split_crack' },
      { tick: SPLIT_IMPACT + SPLIT_STEP * 2, channel: 'vfx', name: 'split_crack' },
      { tick: SPLIT_IMPACT + SPLIT_STEP * 3, channel: 'vfx', name: 'split_crack' },
    ],
    props: { superArmor: false, downChase: true },
  },
  {
    // 혈검 회전베기(Lv25, 'superArmor'): 붉은 검기를 두르고 앞으로 밀고 나가며 9번 돈다. 반경 7m 몹을 끌어오고 슈퍼아머라 보스 강타에도 끊기지 않는다.
    // 칼날 폭풍(제자리·흡입 중심)과 달리 이동하며 무리를 쓸고 보스 패턴 사이를 뚫는 용도
    id: 8, key: '', name: '혈검 회전베기', clip: 'atk4',
    cooldown: ms(14000), mpCost: 75, duration: SPIN_DURATION, cancelAt: 0.6,
    unlock: { kind: 'level', level: SKILL_UNLOCK_LEVEL.bloodSpin },
    motion: { kind: 'dash', distance: 4.5 },
    pull: { radius: 7, speed: 9, fromTick: 0, toTick: SPIN_DURATION - SPIN_EVERY },
    hitboxes: [{ startTick: 0, endTick: SPIN_EVERY * 8, everyTicks: SPIN_EVERY, shape: { kind: 'circle', radius: 4.2 }, mult: 0.95, slash: { range: 4.2, arcDeg: 360, finisher: false } }],
    cues: [
      { tick: 0, channel: 'vfx', name: 'spin_aura' },
      { tick: 0, channel: 'sfx', name: 'spin_start' },
      { tick: SPIN_EVERY * 3, channel: 'vfx', name: 'spin_ring' },
      { tick: SPIN_EVERY * 6, channel: 'vfx', name: 'spin_ring' },
      { tick: SPIN_EVERY * 8, channel: 'vfx', name: 'spin_finish' },
      { tick: SPIN_EVERY * 8, channel: 'sfx', name: 'spin_finish' },
      { tick: SPIN_EVERY * 8, channel: 'camera', name: 'punch_mid' },
    ],
    props: { superArmor: true },
  },
];

// ── 전직 스킬(C10-5, PRD 3A.1 Lv30 전직 I·Lv40 전직 II, docs/SKILL-TREE.md 13절) ──
// 스킬 번호 9 = 전직 I, 10 = 전직 II. 갈래(0 블러드 버서커 · 1 소드 마스터)마다 타임라인이 다르다(registry.heroSkills의 job 인자).
// 해금은 레벨이 아니라 전직 영수증(content/jobQuest.ts)이다: kept 비트로 들어온다(unlock.kind 'level'은 표시용 필요 레벨).
// 기존 여덟 스킬과 역할이 겹치지 않게: 버서커 = 슈퍼아머로 무리 한가운데서 버티는 근접 폭발, 소드 마스터 = 먼 거리 검기·전방 장판.
/** 피의 폭주: 0.9초. 0.3초 동안 반경 6.5m 몹을 발밑으로 끌고(포효) 원형 2타 → 마지막 대폭발(마무리 계열) */
const RAGE_DURATION = ms(900);
const RAGE_HIT1 = ms(300);
const RAGE_HIT2 = ms(480);
/** 흡혈 참격: 0.8초. 6m 돌진 경로 → 끝에서 200° 대참격(새 판정 묶음) */
const DRAIN_DURATION = ms(800);
const DRAIN_DASH = ms(380);
/** 검기 방출: 0.7초. 130ms 간격 검기 3발(각각 새 판정 묶음 — 같은 몹도 세 번 맞는다) */
const WAVES_DURATION = ms(700);
const WAVES_FIRST = ms(150);
const WAVES_EVERY = ms(130);
/** 천검 낙하: 1.0초. 선딜 250ms 뒤 120ms마다 전방 4구간(0~14m)에 검이 떨어지고 마지막에 전 구간 일제 낙하 */
const RAIN_DURATION = ms(1000);
const RAIN_FIRST = ms(250);
const RAIN_EVERY = ms(120);
const RAIN_SEG = 3.5;

const JOB_LV1 = 30;
const JOB_LV2 = 40;

/** 블러드 버서커 전직 스킬(9·10) */
export const KAEL_JOB_BERSERKER: readonly [SkillTimeline, SkillTimeline] = [
  {
    id: 9, key: '', name: '피의 폭주', clip: 'atk4',
    cooldown: ms(15000), mpCost: 80, duration: RAGE_DURATION, cancelAt: 0.6,
    unlock: { kind: 'level', level: JOB_LV1 },
    pull: { radius: 6.5, speed: 11, fromTick: 0, toTick: RAGE_HIT1 },
    hitboxes: [
      { startTick: RAGE_HIT1, endTick: RAGE_HIT1, shape: { kind: 'circle', radius: 4.8 }, mult: 1.7, slash: { range: 4.8, arcDeg: 360, finisher: false } },
      { startTick: RAGE_HIT2, endTick: RAGE_HIT2, freshPulse: true, shape: { kind: 'circle', radius: 4.8 }, mult: 1.7, slash: { range: 4.8, arcDeg: 360, finisher: false } },
      { startTick: RAGE_DURATION - 1, endTick: RAGE_DURATION - 1, freshPulse: true, shape: { kind: 'circle', radius: 5.8 }, mult: 3.6, grants: { finisher: true }, slash: { range: 5.8, arcDeg: 360, finisher: true } },
    ],
    cues: [
      { tick: 0, channel: 'vfx', name: 'spin_aura' },
      { tick: 0, channel: 'sfx', name: 'spin_start' },
      { tick: 0, channel: 'vfx', name: 'storm_range' },
      { tick: RAGE_HIT1, channel: 'vfx', name: 'spin_ring' },
      { tick: RAGE_HIT1, channel: 'camera', name: 'punch_mid' },
      { tick: RAGE_HIT2, channel: 'vfx', name: 'spin_ring' },
      { tick: RAGE_DURATION - 1, channel: 'vfx', name: 'fall_shockwave' },
      { tick: RAGE_DURATION - 1, channel: 'vfx', name: 'storm_finish' },
      { tick: RAGE_DURATION - 1, channel: 'sfx', name: 'skill3_land' },
      { tick: RAGE_DURATION - 1, channel: 'camera', name: 'punch_heavy' },
    ],
    props: { superArmor: true },
  },
  {
    id: 10, key: '', name: '흡혈 참격', clip: 'dash',
    cooldown: ms(18000), mpCost: 90, duration: DRAIN_DURATION, cancelAt: 0.6,
    unlock: { kind: 'level', level: JOB_LV2 },
    motion: { kind: 'dash', distance: 6 },
    hitboxes: [
      { startTick: 0, endTick: DRAIN_DASH, shape: { kind: 'path', width: 3.8 }, mult: 2.2, slash: { range: 2, arcDeg: 90, finisher: false, firstOnly: true } },
      { startTick: DRAIN_DURATION - 1, endTick: DRAIN_DURATION - 1, freshPulse: true, shape: { kind: 'cone', range: 4.4, arcDeg: 200 }, mult: 4.4, grants: { finisher: true }, slash: { range: 4.4, arcDeg: 200, finisher: true } },
    ],
    cues: [
      { tick: 0, channel: 'vfx', name: 'dash_kick' },
      { tick: 0, channel: 'sfx', name: 'skill_swing' },
      { tick: 6, channel: 'vfx', name: 'dash_streak' },
      { tick: 12, channel: 'vfx', name: 'dash_streak' },
      { tick: DRAIN_DASH, channel: 'vfx', name: 'crescent_charge' },
      { tick: DRAIN_DURATION - 1, channel: 'vfx', name: 'split_impact' },
      { tick: DRAIN_DURATION - 1, channel: 'sfx', name: 'split_impact' },
      { tick: DRAIN_DURATION - 1, channel: 'camera', name: 'punch_heavy' },
    ],
    props: { superArmor: true },
  },
];

/** 소드 마스터 전직 스킬(9·10) */
export const KAEL_JOB_SWORDMASTER: readonly [SkillTimeline, SkillTimeline] = [
  {
    id: 9, key: '', name: '검기 방출', clip: 'atk2',
    cooldown: ms(14000), mpCost: 75, duration: WAVES_DURATION, cancelAt: 0.55,
    unlock: { kind: 'level', level: JOB_LV1 },
    hitboxes: [0, 1, 2].map((i) => ({
      startTick: WAVES_FIRST + WAVES_EVERY * i, endTick: WAVES_FIRST + WAVES_EVERY * i, freshPulse: i > 0,
      shape: { kind: 'wave' as const, length: CRESCENT.length + 2, width: CRESCENT.width, travelTicks: CRESCENT.travelTicks }, mult: 2.1,
    })),
    cues: [
      { tick: 0, channel: 'vfx', name: 'crescent_charge' },
      ...[0, 1, 2].flatMap((i) => [
        { tick: WAVES_FIRST + WAVES_EVERY * i, channel: 'vfx' as const, name: 'crescent_wave' },
        { tick: WAVES_FIRST + WAVES_EVERY * i, channel: 'sfx' as const, name: 'skill4_wave' },
      ]),
      { tick: WAVES_FIRST + WAVES_EVERY * 2, channel: 'camera', name: 'punch_mid' },
    ],
    props: { superArmor: false },
  },
  {
    id: 10, key: '', name: '천검 낙하', clip: 'atk3',
    cooldown: ms(18000), mpCost: 90, duration: RAIN_DURATION, cancelAt: 0.6,
    unlock: { kind: 'level', level: JOB_LV2 },
    hitboxes: [
      ...[0, 1, 2, 3].map((i) => ({
        startTick: RAIN_FIRST + RAIN_EVERY * i, endTick: RAIN_FIRST + RAIN_EVERY * i,
        shape: { kind: 'line' as const, from: RAIN_SEG * i, to: RAIN_SEG * (i + 1), width: 4.0 }, mult: 1.8,
      })),
      { startTick: RAIN_DURATION - 1, endTick: RAIN_DURATION - 1, freshPulse: true, shape: { kind: 'line', from: 0, to: RAIN_SEG * 4, width: 4.4 }, mult: 4.4, grants: { finisher: true } },
    ],
    cues: [
      { tick: 0, channel: 'vfx', name: 'split_charge' },
      { tick: 0, channel: 'sfx', name: 'skill_swing' },
      ...[0, 1, 2, 3].map((i) => ({ tick: RAIN_FIRST + RAIN_EVERY * i, channel: 'vfx' as const, name: 'split_crack' })),
      { tick: RAIN_FIRST, channel: 'sfx', name: 'split_impact' },
      { tick: RAIN_DURATION - 1, channel: 'vfx', name: 'split_impact' },
      { tick: RAIN_DURATION - 1, channel: 'sfx', name: 'split_impact' },
      { tick: RAIN_DURATION - 1, channel: 'camera', name: 'punch_heavy' },
    ],
    props: { superArmor: false, downChase: true },
  },
];

/** 갈래별 카엘 스킬 표(1..10). 0 = 블러드 버서커, 1 = 소드 마스터 */
export const KAEL_SKILLS_BY_JOB: readonly [readonly SkillTimeline[], readonly SkillTimeline[]] = [
  [...KAEL_SKILLS, ...KAEL_JOB_BERSERKER],
  [...KAEL_SKILLS, ...KAEL_JOB_SWORDMASTER],
];
